import { useState, useEffect, useRef } from 'react';
import { adminApi } from '../lib/api';

// Modal for uploading a product image from the admin Catalog tab.
// The browser does the heavy lifting:
//   1. FileReader reads any image format the browser supports (JPEG, PNG,
//      HEIC on iOS Safari, GIF, WebP, etc.).
//   2. <img> element loads it, then we draw to a Canvas resized to 400px
//      wide (matching the existing scripts/upload-product-images.py
//      output). Aspect ratio is preserved.
//   3. Canvas.toDataURL('image/jpeg', 0.85) gives us a compact JPEG.
//   4. POST as a base64 data URL — typical end size is ~30–80 KB even
//      from a phone-camera photo, well under API Gateway's 10 MB limit.
//
// Server-side validation in handleAdminUploadProductImage caps at 5 MB
// regardless, so a misbehaving client can't blow up Lambda.
export default function ProductImageUploadModal({ product, onClose, onSuccess }) {
  const [pickedFile, setPickedFile] = useState(null);
  const [previewDataUrl, setPreviewDataUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  // Auto-focus the file input when the modal opens — saves a click.
  useEffect(() => { inputRef.current?.click?.(); /* no-op on iOS, fine on desktop */ }, []);

  async function handleFile(file) {
    if (!file) return;
    setError('');
    setBusy(false);
    if (!file.type.startsWith('image/')) {
      setError('Please pick an image file.');
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      // Hard upper bound on the source file. We resize after this, so
      // even a 25 MB phone photo will become ~50 KB on upload — but we
      // still cap to avoid running the user out of memory on weak phones.
      setError('Image is too large (>25 MB). Try a smaller original.');
      return;
    }
    try {
      const sourceDataUrl = await readFileAsDataUrl(file);
      const jpeg = await resizeToJpeg(sourceDataUrl, 400, 0.85);
      setPickedFile(file);
      setPreviewDataUrl(jpeg);
    } catch (e) {
      setError(e.message || 'Could not process the image.');
    }
  }

  async function handleUpload() {
    if (!previewDataUrl || busy) return;
    setBusy(true);
    setError('');
    try {
      const result = await adminApi.uploadProductImage(product.sku, previewDataUrl);
      onSuccess(result);
      onClose();
    } catch (e) {
      setError(e.message || 'Upload failed.');
      setBusy(false);
    }
  }

  async function handleDelete() {
    if (busy) return;
    if (!window.confirm(`Remove the image from "${product.name}"? Customers will no longer see a thumbnail for this item.`)) return;
    setBusy(true);
    setError('');
    try {
      await adminApi.deleteProductImage(product.sku);
      onSuccess({ sku: product.sku, hasImage: false, deleted: true });
      onClose();
    } catch (e) {
      setError(e.message || 'Delete failed.');
      setBusy(false);
    }
  }

  // Existing image, if any. Append a cache-buster matching what the row
  // shows so we don't see a stale image after a previous re-upload in
  // the same session.
  const existingSrc = product.hasImage && product.sku
    ? `/images/products/${product.sku}.jpg${product._imgBust ? `?v=${product._imgBust}` : ''}`
    : null;

  return (
    <div style={s.backdrop} onClick={onClose}>
      <div style={s.modal} onClick={e => e.stopPropagation()}>
        <h3 style={s.title}>Upload product image</h3>
        <p style={s.subtitle}>
          {product.name}
          {product.sku && <span style={s.sku}>{product.sku}</span>}
        </p>

        <div style={s.previewRow}>
          <PreviewCard
            label={existingSrc ? 'Current' : 'No image yet'}
            src={existingSrc}
            placeholder="—"
          />
          <div style={s.arrow}>→</div>
          <PreviewCard
            label="New"
            src={previewDataUrl}
            placeholder="Pick a file"
          />
        </div>

        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          onChange={e => handleFile(e.target.files?.[0])}
          style={s.fileInput}
        />

        <p style={s.hint}>
          Any image format works (JPG, PNG, HEIC). We resize to 400px wide
          and convert to JPEG before uploading — typical upload is under 100 KB.
        </p>

        {error && <div style={s.error}>{error}</div>}

        <div style={s.actions}>
          {/* Remove only available when there's an existing image to
              remove. Sits on the left, separated from the right-side
              cluster so it's clearly destructive and not adjacent to
              the primary action. */}
          {product.hasImage && (
            <button
              type="button"
              style={s.removeBtn}
              onClick={handleDelete}
              disabled={busy}
              title="Remove the existing image"
            >
              Remove image
            </button>
          )}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.6rem' }}>
            <button style={s.cancelBtn} onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button
              style={{ ...s.uploadBtn, ...(!previewDataUrl || busy ? s.uploadBtnDisabled : {}) }}
              onClick={handleUpload}
              disabled={!previewDataUrl || busy}
            >
              {busy ? 'Uploading…' : 'Upload'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PreviewCard({ label, src, placeholder }) {
  return (
    <div style={s.previewCard}>
      {src ? (
        <img src={src} alt="" style={s.previewImg} />
      ) : (
        <div style={s.previewEmpty}>{placeholder}</div>
      )}
      <div style={s.previewLabel}>{label}</div>
    </div>
  );
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Could not read the file.'));
    reader.readAsDataURL(file);
  });
}

function resizeToJpeg(sourceDataUrl, maxWidth, quality) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxWidth / img.width);
      const w = Math.max(1, Math.round(img.width * scale));
      const h = Math.max(1, Math.round(img.height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      // Fill white in case the source is transparent (PNG) — produces
      // a clean JPEG instead of the spec-mandated black background.
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, w, h);
      ctx.drawImage(img, 0, 0, w, h);
      try {
        resolve(canvas.toDataURL('image/jpeg', quality));
      } catch (e) {
        reject(new Error('Could not export JPEG (possibly a tainted canvas).'));
      }
    };
    img.onerror = () => reject(new Error('That file does not appear to be a valid image.'));
    img.src = sourceDataUrl;
  });
}

const s = {
  backdrop: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 300, padding: '1rem',
  },
  modal: {
    background: '#fff', borderRadius: 12,
    width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto',
    padding: '1.5rem 1.5rem 1.25rem',
    boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
  },
  title: { fontSize: '1.15rem', fontWeight: 700, color: '#222', margin: '0 0 0.35rem' },
  subtitle: { fontSize: '0.85rem', color: 'rgba(0,0,0,0.6)', margin: '0 0 1.25rem', lineHeight: 1.45 },
  sku: { fontFamily: 'monospace', marginLeft: 6, color: 'rgba(0,0,0,0.4)' },

  previewRow: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', margin: '0 0 1rem' },
  previewCard: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.4rem', flex: 1, maxWidth: 180 },
  previewImg: { width: '100%', maxWidth: 160, height: 160, objectFit: 'contain', background: '#f7f5f2', borderRadius: 6, border: '1px solid #e0e0e0' },
  previewEmpty: { width: 160, height: 160, background: '#f7f5f2', border: '1px dashed #ddd', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.85rem', color: 'rgba(0,0,0,0.35)' },
  previewLabel: { fontSize: '0.72rem', color: 'rgba(0,0,0,0.5)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 },
  arrow: { fontSize: '1.25rem', color: 'rgba(0,0,0,0.3)', flexShrink: 0 },

  fileInput: { width: '100%', padding: '0.5rem 0', fontFamily: 'inherit', fontSize: '0.9rem' },
  hint: { fontSize: '0.78rem', color: 'rgba(0,0,0,0.5)', lineHeight: 1.45, margin: '0.4rem 0 0' },

  error: { background: 'rgba(231,76,60,0.08)', border: '1px solid rgba(231,76,60,0.25)', borderRadius: 6, padding: '0.6rem 0.8rem', color: '#c0392b', fontSize: '0.85rem', marginTop: '0.85rem' },

  actions: { display: 'flex', alignItems: 'center', gap: '0.6rem', marginTop: '1.25rem', flexWrap: 'wrap' },
  cancelBtn: { background: 'none', border: '1px solid #ddd', color: '#444', borderRadius: 6, padding: '0.6rem 1.2rem', fontSize: '0.9rem', cursor: 'pointer', fontFamily: 'inherit' },
  uploadBtn: { background: '#A50B18', color: '#fff', border: 'none', borderRadius: 6, padding: '0.6rem 1.4rem', fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
  uploadBtnDisabled: { background: 'rgba(165,11,24,0.5)', cursor: 'not-allowed' },
  removeBtn: {
    background: 'none', border: 'none',
    color: '#c0392b', fontSize: '0.85rem', fontWeight: 500,
    cursor: 'pointer', fontFamily: 'inherit',
    padding: '0.4rem 0.5rem',
    textDecoration: 'underline',
  },
};
